// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { WorkCell } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { GridMenu } from 'components/Mantine/GridMenu';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { isNotNil } from 'helpers/isNotNil';

import { CreateEditWorkCellModal } from './CreateEditWorkCellModal';
import { useWorkCellSetup } from './WorkCellSetupContext';

type Props = {
  canDelete: boolean;
  workCell: WorkCell;
  workCellUpdated: (workCell: WorkCell) => void;
};

export const EditWorkCellMenu = ({ workCell, workCellUpdated, canDelete }: Props) => {
  const {
    updateWorkCell,
    deleteWorkCell,
    mutationRunning,
    serverSideGridProps: { gridApi },
  } = useWorkCellSetup();
  const [saving, setSaving] = useState<'editing' | 'deleting'>();
  return (
    <GridMenu
      options={[
        'Edit',
        {
          value: 'Delete',
          hidden: !canDelete,
        },
      ]}
      loading={isNotNil(saving)}
      disabled={mutationRunning}
    >
      {(selected, resetMenu) => (
        <>
          <CreateEditWorkCellModal
            opened={selected === 'Edit'}
            onClose={resetMenu}
            workCell={workCell}
            onSubmit={async (values) => {
              setSaving('editing');
              resetMenu();
              await updateWorkCell({ ...values, workCellId: workCell.workCellId })
                .then(workCellUpdated)
                .finally(() => setSaving(undefined));
            }}
          />
          <WrappedModal
            title="Delete work cell"
            opened={selected === 'Delete'}
            onClose={resetMenu}
            loading={mutationRunning}
            confirmationText="Delete"
            onConfirm={async () => {
              setSaving('deleting');
              resetMenu();
              await deleteWorkCell(workCell.workCellId)
                .then(() => {
                  refreshGrid(gridApi);
                  notifications.show({
                    title: 'Successfully deleted',
                    message: `Deleted work cell ${workCell.workCellName}`,
                    color: 'green',
                  });
                })
                .finally(() => setSaving(undefined));
            }}
          >
            <Text>
              Are you sure you want to delete <b>{workCell.workCellName}</b>?
            </Text>
          </WrappedModal>
        </>
      )}
    </GridMenu>
  );
};
